import {
  Badge,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from "@izicvs/ui";
import { t } from "@lingui/macro";
import {
  ArrowClockwise,
  BellRinging,
  BookmarkSimple,
  Briefcase,
  CircleNotch,
  MagnifyingGlass,
  Sparkle,
  TrashSimple,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router";

import { ConfirmDelete } from "@/client/components/confirm-delete";
import { ThemeSwitch } from "@/client/components/theme-switch";
import { toast } from "@/client/hooks/use-toast";
import { resumeToPlainText } from "@/client/services/ai/job-match";
import { useApplications, useCreateApplication } from "@/client/services/applications";
import type { JobListing, SearchProfile } from "@/client/services/jobs";
import type { PreparedApplication } from "@/client/services/jobs";
import {
  useDeleteProfile,
  useDigest,
  useFollowSearch,
  useJobs,
  useProfiles,
  useRunProfile,
  useSearchJobs,
  useToggleProfile,
} from "@/client/services/jobs";
import { usePrepareApplication } from "@/client/services/jobs";
import type { ScoredJob } from "@/client/services/jobs/fit";
import { rankJobs } from "@/client/services/jobs/fit";
import { useResumes } from "@/client/services/resume";

import { LoadingPanel } from "../_components/loading-panel";
import { NotificationsBell } from "../_components/notifications";
import { ApplicationDialog } from "./_components/application-dialog";
import { fitVariant, JobCard, JobCardSkeleton } from "./_components/job-card";

export const OpportunitiesPage = () => {
  const navigate = useNavigate();
  const { search, loading: searching } = useSearchJobs();
  const { follow, loading: following } = useFollowSearch();
  const { profiles, loading: profilesLoading } = useProfiles();
  const { run } = useRunProfile();
  const { toggle } = useToggleProfile();
  const { remove } = useDeleteProfile();
  const { resumes } = useResumes();
  const { create: trackApplication } = useCreateApplication();
  const { applications } = useApplications();
  const { digest, loading: digestLoading } = useDigest();
  const { prepare, loading: preparing } = usePrepareApplication();

  const [preparedApp, setPreparedApp] = useState<PreparedApplication | null>(null);
  const [preparedFor, setPreparedFor] = useState<{ jobId: string; resumeId: string } | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [preparingJobId, setPreparingJobId] = useState<string | null>(null);

  const trackedJobIds = useMemo(
    () => new Set((applications ?? []).map((application) => application.jobId).filter(Boolean)),
    [applications],
  );

  const [query, setQuery] = useState("");
  const [location, setLocation] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [matchResumeId, setMatchResumeId] = useState("");

  const { jobs, total, loading } = useJobs({ query: activeQuery, take: 60 });

  useEffect(() => {
    if (!matchResumeId && resumes && resumes.length > 0) setMatchResumeId(resumes[0].id);
  }, [resumes, matchResumeId]);

  // Rank the loaded offers by fit against the selected resume (pure code).
  const matchResume = resumes?.find((item) => item.id === matchResumeId);
  const ranked: ScoredJob[] = useMemo(() => {
    if (!jobs) return [];
    if (!matchResume) return jobs.map((job) => ({ ...job, fit: 0 }));
    return rankJobs(jobs, resumeToPlainText(matchResume.data));
  }, [jobs, matchResume]);

  const onSearch = async () => {
    if (!query.trim()) return;

    try {
      const result = await search({ query: query.trim(), location: location.trim() || undefined });
      setActiveQuery(query.trim());
      toast({
        variant: "success",
        title: t`Search complete`,
        description: t`${result.saved} offers found across ${result.sources.length} sources.`,
      });
    } catch (error) {
      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: (error as Error).message,
      });
    }
  };

  const onFollow = async () => {
    if (!query.trim()) return;

    try {
      await follow({ keywords: query.trim(), location: location.trim() || undefined });
      setActiveQuery(query.trim());
      toast({
        variant: "success",
        title: t`Search followed`,
        description: t`We'll scrape it automatically and alert you to new offers.`,
      });
    } catch (error) {
      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: (error as Error).message,
      });
    }
  };

  const onOpenProfile = (profile: SearchProfile) => {
    setQuery(profile.keywords);
    setLocation(profile.location ?? "");
    setActiveQuery(profile.keywords);
  };

  const onAnalyze = (job: JobListing) => {
    // Prefill Job Match with this offer and carry its metadata so the result
    // can be tracked as a fully-linked application.
    const jobDescription = [job.title, job.company, job.location, job.description]
      .filter(Boolean)
      .join("\n");
    void navigate("/dashboard/jobs", {
      state: {
        jobDescription,
        jobMeta: { title: job.title, company: job.company, location: job.location, url: job.url },
      },
    });
  };

  const onPrepare = async (job: JobListing) => {
    if (!matchResumeId) {
      toast({ variant: "error", title: t`Select a resume to match against first.` });
      return;
    }
    setPreparingJobId(job.id);
    try {
      const prepared = await prepare({ jobId: job.id, resumeId: matchResumeId });
      setPreparedApp(prepared);
      setPreparedFor({ jobId: job.id, resumeId: matchResumeId });
      setDialogOpen(true);
    } catch (error) {
      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: (error as Error).message,
      });
    } finally {
      setPreparingJobId(null);
    }
  };

  const onTrack = async (job: JobListing) => {
    if (trackedJobIds.has(job.id)) return;
    try {
      await trackApplication({
        jobTitle: job.title,
        company: job.company,
        location: job.location ?? undefined,
        url: job.url,
        jobId: job.id,
      });
      toast({ variant: "success", title: t`Added to your applications` });
    } catch (error) {
      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: (error as Error).message,
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>
          {t`Offers`} - {t`Izicvs`}
        </title>
      </Helmet>

      <div className="space-y-6">
        {/* Header: title + intro, with the shared notification + theme cluster. */}
        <div className="flex items-start justify-between gap-4">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-1"
          >
            <h1 className="flex items-center gap-x-2 text-3xl font-bold tracking-tight lg:text-4xl">
              <Briefcase weight="fill" className="text-primary" />
              {t`Offers`}
            </h1>
            <p className="max-w-xl text-sm opacity-75">
              {t`Search job offers from multiple sources, then analyze any of them against your resume.`}
            </p>
          </motion.div>

          <div className="flex shrink-0 items-center gap-x-3">
            <NotificationsBell />
            <ThemeSwitch />
          </div>
        </div>

        {profilesLoading || digestLoading ? (
          <LoadingPanel />
        ) : (
          <>
            {digest && digest.length > 0 && (
              <div className="grid gap-y-2 rounded-2xl border border-primary/30 bg-primary/5 p-4">
                <p className="flex items-center gap-x-2 text-xs font-medium uppercase text-primary">
                  <Sparkle weight="fill" size={14} />
                  {t`Your top matches today`}
                </p>
                <div className="grid gap-y-1.5">
                  {digest.map(({ job, fit }) => (
                    <button
                      key={job.id}
                      type="button"
                      className="flex items-center gap-x-3 rounded-lg border bg-background p-3 text-left transition-colors hover:bg-secondary-accent/20"
                      onClick={() => {
                        onAnalyze(job);
                      }}
                    >
                      <Badge variant={fitVariant(fit)} className="shrink-0 font-bold">
                        {t`${fit}% match`}
                      </Badge>
                      <div className="min-w-0 flex-1">
                        <p className="line-clamp-1 text-sm font-medium">{job.title}</p>
                        <p className="text-xs opacity-60">{job.company}</p>
                      </div>
                      <Sparkle className="shrink-0 opacity-40" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Search panel */}
            <div className="grid gap-y-3 rounded-2xl border bg-secondary-accent/20 p-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>{t`Keywords`}</Label>
                  <Input
                    value={query}
                    placeholder={t`e.g. React developer`}
                    onChange={(event) => {
                      setQuery(event.target.value);
                    }}
                    onKeyDown={(event) => event.key === "Enter" && onSearch()}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t`Location (optional)`}</Label>
                  <Input
                    value={location}
                    placeholder={t`e.g. Paris`}
                    onChange={(event) => {
                      setLocation(event.target.value);
                    }}
                    onKeyDown={(event) => event.key === "Enter" && onSearch()}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                <Button
                  className="w-full sm:w-auto"
                  disabled={searching || !query.trim()}
                  onClick={onSearch}
                >
                  {searching ? (
                    <CircleNotch className="mr-2 animate-spin" />
                  ) : (
                    <MagnifyingGlass className="mr-2" />
                  )}
                  {searching ? t`Searching...` : t`Search offers`}
                </Button>
                <Button
                  variant="secondary"
                  className="w-full sm:w-auto"
                  disabled={following || !query.trim()}
                  onClick={onFollow}
                >
                  {following ? (
                    <CircleNotch className="mr-2 animate-spin" />
                  ) : (
                    <BookmarkSimple className="mr-2" />
                  )}
                  {t`Follow this search`}
                </Button>
              </div>
            </div>

            {profiles && profiles.length > 0 && (
              <div className="grid gap-y-2 rounded-2xl border p-4">
                <p className="flex flex-wrap items-center gap-x-2 text-xs font-medium uppercase opacity-60">
                  <BellRinging size={14} />
                  {t`Followed searches`}
                  <span className="font-normal normal-case opacity-75">
                    · {t`scraped automatically every 6 hours`}
                  </span>
                </p>
                <div className="grid gap-y-1.5">
                  {profiles.map((profile) => (
                    <div
                      key={profile.id}
                      className="flex items-center gap-x-2 rounded-lg border p-3 transition-colors hover:bg-secondary-accent/20 sm:gap-x-3"
                    >
                      <button
                        type="button"
                        className="min-w-0 flex-1 text-left"
                        onClick={() => {
                          onOpenProfile(profile);
                        }}
                      >
                        <p className="flex items-center gap-x-2 text-sm font-medium">
                          <span className="line-clamp-1">{profile.keywords}</span>
                          {profile.lastNewCount > 0 && (
                            <Badge variant="success" className="shrink-0">
                              {t`${profile.lastNewCount} new`}
                            </Badge>
                          )}
                        </p>
                        <p className="text-xs opacity-60">
                          {[profile.location, t`${profile.totalMatches} offers`]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      </button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="hidden shrink-0 sm:inline-flex"
                        title={t`Refresh now`}
                        onClick={() => void run(profile.id)}
                      >
                        <ArrowClockwise />
                      </Button>
                      <Switch
                        checked={profile.active}
                        onCheckedChange={(active) => void toggle({ id: profile.id, active })}
                      />
                      <ConfirmDelete
                        title={t`Delete this followed search?`}
                        onConfirm={() => void remove(profile.id)}
                      >
                        <Button size="icon" variant="ghost" className="shrink-0">
                          <TrashSimple />
                        </Button>
                      </ConfirmDelete>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Results */}
            {loading ? (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <JobCardSkeleton key={i} />
                ))}
              </div>
            ) : ranked.length > 0 ? (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs font-medium uppercase opacity-60">
                    {matchResume ? t`${total} offers · ranked for you` : t`${total} offers`}
                  </p>
                  {resumes && resumes.length > 0 && (
                    <div className="flex items-center gap-x-2">
                      <span className="text-xs opacity-60">{t`Match against`}</span>
                      <Select value={matchResumeId} onValueChange={setMatchResumeId}>
                        <SelectTrigger className="h-8 w-48 gap-x-2 rounded-full text-xs sm:w-72 [&>span]:min-w-0 [&>span]:flex-1 [&>span]:truncate [&>span]:text-left">
                          <SelectValue placeholder={t`Resume`} />
                        </SelectTrigger>
                        <SelectContent>
                          {resumes.map((resume) => (
                            <SelectItem key={resume.id} value={resume.id}>
                              {resume.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {ranked.map((job) => (
                    <JobCard
                      key={job.id}
                      job={job}
                      fit={matchResume ? job.fit : null}
                      tracked={trackedJobIds.has(job.id)}
                      preparing={preparing && preparingJobId === job.id}
                      onAnalyze={onAnalyze}
                      onTrack={onTrack}
                      onPrepare={onPrepare}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-y-4 rounded-2xl border border-dashed py-16 text-center">
                <div className="flex size-16 items-center justify-center rounded-full bg-secondary/60">
                  <Briefcase size={30} className="opacity-70" />
                </div>
                <div className="space-y-1">
                  <h2 className="text-lg font-semibold">{t`No offers yet`}</h2>
                  <p className="max-w-sm text-sm opacity-60">
                    {t`Run a search above to pull in fresh listings from multiple job boards.`}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ApplicationDialog
        application={preparedApp}
        jobId={preparedFor?.jobId ?? null}
        resumeId={preparedFor?.resumeId ?? null}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </>
  );
};
